import { useEffect, useRef } from 'react';

// useFocusTrap — keep keyboard focus inside an open overlay (modal / lightbox) for a11y.
// On mount (when `active`) it focuses the first focusable inside the container, wraps Tab / Shift+Tab at the
// ends so focus can't escape behind the backdrop, and restores focus to whatever was focused before on close.
// Attach the returned ref to the dialog container. Pairs with the existing Escape-to-close + role="dialog".
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(active: boolean = true) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const container = ref.current;
    if (!active || !container) return;

    const restoreTo = document.activeElement as HTMLElement | null;
    // Only visible focusables (offsetParent is null for display:none / detached nodes).
    const focusables = () =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null);

    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey && (activeEl === first || !container.contains(activeEl))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKey);
    return () => {
      container.removeEventListener('keydown', onKey);
      restoreTo?.focus?.();
    };
  }, [active]);

  return ref;
}
